import { useServices, useToast } from '@/app/providers';
import { Select } from '@/presentation/components/atoms/Select';
import { useI18n } from '@/shared/i18n';
import type { Notification, NotificationChannel, NotificationPriority, NotificationStatus } from '@yawi/domain';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import {
  Bell,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Filter,
  Inbox,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Smartphone,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRouteFromNotification } from '@/utils/notificationDeepLink';

type FilterStatus = 'all' | 'unread' | 'read';
type FilterChannel = 'all' | NotificationChannel;

const ITEMS_PER_PAGE = 10;

const getChannelIcon = (channel: NotificationChannel) => {
  switch (channel) {
    case 'EMAIL':
      return <Mail size={14} />;
    case 'PUSH':
      return <Smartphone size={14} />;
    case 'SMS':
      return <Phone size={14} />;
    case 'WHATSAPP':
      return <MessageCircle size={14} />;
    case 'IN_APP':
    default:
      return <Bell size={14} />;
  }
};

const CHANNEL_LABELS: Record<'es' | 'en', Record<NotificationChannel, string>> = {
  es: { IN_APP: 'En App', EMAIL: 'Correo', PUSH: 'Push', SMS: 'SMS', WHATSAPP: 'WhatsApp' },
  en: { IN_APP: 'In App', EMAIL: 'Email', PUSH: 'Push', SMS: 'SMS', WHATSAPP: 'WhatsApp' },
};

const getChannelLabel = (channel: NotificationChannel, locale: 'es' | 'en') =>
  CHANNEL_LABELS[locale]?.[channel] ?? channel;

const PRIORITY_LABELS: Record<'es' | 'en', Record<NotificationPriority, string>> = {
  es: { URGENT: 'Urgente', HIGH: 'Alta', NORMAL: 'Normal', LOW: 'Baja' },
  en: { URGENT: 'Urgent', HIGH: 'High', NORMAL: 'Normal', LOW: 'Low' },
};

const getPriorityLabel = (priority: NotificationPriority, locale: 'es' | 'en') =>
  PRIORITY_LABELS[locale]?.[priority] ?? priority;

const getPriorityStyles = (priority: NotificationPriority) => {
  switch (priority) {
    case 'URGENT':
      return {
        border: 'border-l-red-500',
        bg: 'bg-red-50 dark:bg-red-900/20',
        badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
      };
    case 'HIGH':
      return {
        border: 'border-l-amber-500',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
      };
    case 'NORMAL':
      return {
        border: 'border-l-blue-500',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
      };
    case 'LOW':
    default:
      return {
        border: 'border-l-slate-300',
        bg: 'bg-slate-50 dark:bg-slate-800/50',
        badge: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      };
  }
};

interface NotificationItemProps {
  notification: Notification;
  onNotificationClick: (notification: Notification) => void;
  locale: 'es' | 'en';
}

const NotificationItem = ({ notification, onNotificationClick, locale }: NotificationItemProps) => {
  const isUnread = notification.status !== 'READ';
  const styles = getPriorityStyles(notification.priority);
  const dateLocale = locale === 'es' ? es : enUS;

  const formatTime = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: dateLocale });
    } catch {
      return dateString;
    }
  };

  return (
    <div
      className={`p-4 border border-theme border-l-4 rounded-xl transition-colors cursor-pointer ${styles.border} ${
        isUnread ? 'bg-surface' : 'bg-surface-secondary'
      } hover:bg-surface-secondary`}
      onClick={() => onNotificationClick(notification)}
    >
      <div className="flex items-start gap-3">
        {isUnread && <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles.badge}`}>
              {getPriorityLabel(notification.priority, locale)}
            </span>
            {notification.deliveries.map((d) => (
              <span
                key={d.channel}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-secondary text-theme-secondary border border-theme"
              >
                {getChannelIcon(d.channel)}
                {getChannelLabel(d.channel, locale)}
              </span>
            ))}
          </div>
          <h4 className={`text-sm ${isUnread ? 'font-semibold text-theme' : 'font-medium text-theme-secondary'}`}>
            {notification.title}
          </h4>
          <p className="text-sm text-theme-tertiary mt-1 line-clamp-2">{notification.body}</p>
          <p className="text-xs text-theme-tertiary mt-2">{formatTime(notification.createdAt)}</p>
        </div>
        {isUnread && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNotificationClick(notification);
            }}
            className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors"
            title="Marcar como leída"
          >
            <Check size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export const Notifications = () => {
  const { t, language } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { notificationRepo } = useServices();
  const { currentUser } = useServices();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterChannel, setFilterChannel] = useState<FilterChannel>('all');
  // Contador real del servidor: `notifications` sólo es la página actual, así
  // que derivarlo de ahí rottingía el badge en cuanto había más de una página.
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      // Filtro y paginación ocurren en el servidor: el contador y los botones
      // leen `meta`, que describe el conjunto filtrado, no la página.
      const response = await notificationRepo.getNotifications({
        page,
        limit: ITEMS_PER_PAGE,
        channel: filterChannel === 'all' ? undefined : filterChannel,
        status: filterStatus === 'all' ? undefined : filterStatus.toUpperCase() as 'READ' | 'UNREAD',
      });

      setNotifications(response.data);
      setTotalCount(response.meta.total);

      const unread = await notificationRepo.getUnreadCount().catch(() => 0);
      setUnreadTotal(unread);
    } catch (error) {
      toast.error(t('notifications.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [notificationRepo, page, filterChannel, filterStatus, toast, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.status !== 'READ') {
      setNavigatingId(notification.id);
    }

    const route = getRouteFromNotification(notification.data, notification.type);

    if (!route) {
      if (notification.status !== 'READ') {
        await handleMarkAsRead(notification.id, false);
      }
      setNavigatingId(null);
      return;
    }

    if (!currentUser) {
      sessionStorage.setItem('pendingNotificationRoute', route);
      navigate('/login', { state: { from: route } });
      return;
    }

    try {
      await navigate(route);
      if (notification.status !== 'READ') {
        await handleMarkAsRead(notification.id, false);
      }
    } catch {
      toast.error(t('notifications.errorNavigating') || 'Unable to open that notification');
    } finally {
      setNavigatingId(null);
    }
  };

  const handleMarkAsRead = async (id: string, showToast = true) => {
    try {
      const wasUnread = notifications.find((n) => n.id === id)?.status !== 'READ';
      await notificationRepo.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: 'READ' as NotificationStatus, readAt: new Date().toISOString() } : n))
      );
      if (wasUnread) {
        setUnreadTotal((prev) => Math.max(0, prev - 1));
      }
      if (showToast) {
        toast.success(t('notifications.markedAsRead'));
      }
    } catch (error) {
      toast.error(t('notifications.errorMarkingRead'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationRepo.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, status: 'READ' as NotificationStatus, readAt: new Date().toISOString() }))
      );
      setUnreadTotal(0);
      toast.success(t('notifications.allMarkedAsRead'));
    } catch (error) {
      toast.error(t('notifications.errorMarkingAllRead'));
    }
  };

  const unreadCount = unreadTotal;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const statusFilters: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: t('notifications.filterAll') },
    { value: 'unread', label: t('notifications.filterUnread') },
    { value: 'read', label: t('notifications.filterRead') },
  ];

  const channelFilters: { value: FilterChannel; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: t('notifications.channelAll'), icon: null },
    { value: 'IN_APP', label: t('notifications.channelInApp'), icon: <Bell size={14} /> },
    { value: 'EMAIL', label: t('notifications.channelEmail'), icon: <Mail size={14} /> },
    { value: 'PUSH', label: t('notifications.channelPush'), icon: <Smartphone size={14} /> },
    { value: 'SMS', label: 'SMS', icon: <Phone size={14} /> },
    { value: 'WHATSAPP', label: 'WhatsApp', icon: <MessageCircle size={14} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Bell className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-theme">{t('notifications.title')}</h2>
            <p className="text-sm text-theme-secondary">
              {unreadCount > 0
                ? `${unreadCount} ${t('notifications.filterUnread').toLowerCase()}`
                : t('notifications.allRead')}
            </p>
          </div>
        </div>

        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors text-sm font-medium"
          >
            <CheckCheck size={18} />
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="hidden sm:flex items-center text-theme-tertiary pb-2.5">
          <Filter size={18} className="shrink-0" />
        </div>
        <div className="w-full sm:w-48">
          <Select
            label={language === 'es' ? 'Estado' : 'Status'}
            value={filterStatus}
            onChange={(v) => {
              setFilterStatus(v as FilterStatus);
              setPage(1);
            }}
            options={statusFilters.map((f) => ({ value: f.value, label: f.label }))}
          />
        </div>
        <div className="w-full sm:w-52">
          <Select
            label={language === 'es' ? 'Canal' : 'Channel'}
            value={filterChannel}
            onChange={(v) => {
              setFilterChannel(v as FilterChannel);
              setPage(1);
            }}
            options={channelFilters.map((f) => ({ value: f.value, label: f.label }))}
          />
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16 bg-surface-secondary rounded-xl border border-theme-light">
          <Inbox className="w-16 h-16 text-theme-tertiary mx-auto mb-4 opacity-50" />
          <p className="text-theme-secondary font-medium text-lg">{t('notifications.empty')}</p>
          <p className="text-sm text-theme-tertiary mt-1">
            {filterStatus !== 'all' || filterChannel !== 'all'
              ? t('notifications.tryDifferentFilter')
              : t('notifications.noNotifications')}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onNotificationClick={handleNotificationClick}
                locale={language as 'es' | 'en'}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg bg-surface-secondary border border-theme-light hover:bg-surface-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-theme-secondary">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg bg-surface-secondary border border-theme-light hover:bg-surface-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
